import { Location } from '@angular/common'
import { Component, HostBinding, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { MatPaginator } from '@angular/material/paginator'
import { MatDialog } from '@angular/material/dialog'
import { HttpErrorResponse } from '@angular/common/http'

import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { GrowthToolkitClient } from 'src/app/shared/services/ppv.api'
import { NewMeetingSchedulerComponent } from './new-meeting-scheduler/new-meeting-scheduler.component'
import { MeetingDetailDialogComponent } from './meeting-detail-dialog.component';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { LeaderService } from '../../services/leader.service'

@Component({
   selector: 'leader-toolkit-scheduling',
   templateUrl: './scheduling-page.component.html',
   styleUrls: ['./scheduling-page.component.scss'],
   standalone: false,
})
export class LeaderToolkitSchedulingPage implements OnInit, OnDestroy, AfterViewInit {
   selectedTab: 'upcoming' | 'history' = 'upcoming';
   selectedTabIndex: number = 0;
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   selectedHospitals: any[] = []
   selectedDuration: string = ''
   selectedAttendees: string[] = []
   globalFilter: string = ''
   displayedColumns: any[] = ['subject', 'date',   'duration','hospital', 'actions']
   dataSource!: MatTableDataSource<any>;
   upcomingDataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
   historyDataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
   isLoading: boolean = true
   meetingList: any
   meetingsData: any[] = []
   hospitalList: any[] = []
   meetingStats: any[] = []

   // ViewChild references
   @ViewChild(MatSort) sort!: MatSort
   @ViewChild('upcomingSort') upcomingSort!: MatSort
   @ViewChild('historySort') historySort!: MatSort
   @ViewChild(MatPaginator) paginator!: MatPaginator

   @ViewChild(MatSort) set matSort(ms: MatSort) {
      this.sort = ms
      this.setDataSourceAttributes()
   }

   @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
      this.paginator = mp
      this.setDataSourceAttributes()
   }

   setDataSourceAttributes(): void {
      if (this.dataSource && this.paginator) {
         this.dataSource.paginator = this.paginator
         if (this.selectedTab === 'upcoming' && this.upcomingSort) {
            this.dataSource.sort = this.upcomingSort
         } else if (this.selectedTab === 'history' && this.historySort) {
            this.dataSource.sort = this.historySort
         } else if (this.sort) {
            this.dataSource.sort = this.sort
         }
      }
   }
   constructor(
      public router: Router,
      private route: ActivatedRoute,
      private dialog: MatDialog,
      private leaderService: LeaderService
   ) { }

   private pendingSearchHighlight: { id: number } | null = null
   private destroy$ = new Subject<void>()

   ngOnInit(): void {
      this.getHospitalList();
      this.selectedTab = 'upcoming';
      this.selectedTabIndex = 0;
      this.loadMeetings('upcoming');

      // Pick up the search-highlight intent from NavigationExtras.state set
      // by the search results page. We use in-memory state (NOT query
      // params) so the destination URL stays clean. After consuming, we
      // strip the marker from history.state so a hard refresh doesn't
      // accidentally re-open the popup.
      const state: any = (typeof history !== 'undefined' && history.state) || {}
      const sh = state.searchHighlight
      const id = Number(sh?.id)
      if (id && Number.isFinite(id) && id > 0) {
         this.pendingSearchHighlight = { id }
         try {
            const cleaned = { ...state }
            delete cleaned.searchHighlight
            history.replaceState(cleaned, '')
         } catch { /* non-browser env — ignore */ }
      }

      // Check for meetingId query param in URL (deep link from email notification)
   this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const meetingId = params['meetingId']
      if (meetingId && !this.pendingSearchHighlight) {
         const id = Number(meetingId)
         if (Number.isFinite(id) && id > 0) {
            this.pendingSearchHighlight = { id }
            this.router.navigate([], { queryParams: { meetingId: null }, queryParamsHandling: 'merge', replaceUrl: true })
         }
      }
   })

      this.leaderService.searchHighlightEvent
         .pipe(takeUntil(this.destroy$))
         .subscribe(({ id: highlightId }) => {
            const row =
               this.upcomingDataSource?.data?.find((m: any) => m.meetingId == highlightId) ||
               this.historyDataSource?.data?.find((m: any) => m.meetingId == highlightId);
            if (row) this.openMeetingDetailDialog(row);
         });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   /**
    * After a meetings list loads, apply any search-highlight intent set on
    * NavigationExtras.state by the search results page. Searches both the
    * upcoming + history sources (whichever have been loaded so far). Safe
    * no-op if not found.
    *
    * Past meetings won't be present in the upcoming source on first load;
    * this is intentional — the user can switch to the History tab and the
    * highlight will resolve on the next load (pending stays in place until
    * we find the row).
    */
   private applyPendingSearchHighlight(): void {
      const pending = this.pendingSearchHighlight
      if (!pending) return

      const findIn = (rows: any[] | undefined): any | null => {
         if (!rows || !rows.length) return null
         return rows.find((r) => Number(r?.meetingId) === pending.id) || null
      }

      const row =
         findIn(this.upcomingDataSource?.data) || findIn(this.historyDataSource?.data)
      if (!row) return

      // Consume only when resolved, so a tab switch can still pick it up later.
      this.pendingSearchHighlight = null

      setTimeout(() => {
         const el = document.getElementById('search-row-meeting-' + row.meetingId)
         if (el) {
            try {
               el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            } catch { /* older browsers — ignore */ }
            el.classList.add('search-flash')
            setTimeout(() => el.classList.remove('search-flash'), 2500)
         }
         this.openMeetingDetailDialog(row)
      }, 0)
   }
   onTabChange(index: number): void {
      this.selectedTabIndex = index;
      this.selectedTab = index === 0 ? 'upcoming' : 'history';

      // Reset filters on tab switch
      this.selectedHospitals = [];
      this.selectedDuration = '';
      this.selectedAttendees = [];
      this.globalFilter = '';

      if (this.selectedTab === 'upcoming') {
         this.dataSource = this.upcomingDataSource;
      } else {
         this.dataSource = this.historyDataSource;
      }
      this.setDataSourceAttributes();
      setTimeout(() => {
         if (this.dataSource && this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
         }
      }, 0);
      this.loadMeetings(this.selectedTab);
   }

   ngAfterViewInit(): void {
      // No-op: paginator and sort are set after dataSource is initialized
   }

   getHospitalList(): void {
      this.leaderService.getHospitalList().subscribe({
         next: (res: any) => {
            this.hospitalList = res.data;
         },
         error: (error: HttpErrorResponse) => {
            console.warn('Scheduling hospital list failed', {
               route: this.router.url,
               status: error?.status,
               message: error?.message,
            });
         }
      });
   }

   loadMeetings(type: string): void {
      this.isLoading = true;
      // The History tab is 'history' in the UI, but the API only understands
      // 'upcoming' | 'past' | 'all'. Map it so past meetings aren't dropped.
      const apiFilter = type === 'history' ? 'past' : type;
      this.leaderService.getScheduledMeetings(apiFilter).subscribe({
         next: (response: any) => {
            if (!response || !response.data) {
               this.meetingList = []
               this.isLoading = false;
               return;
            }
            this.updateStats(response.data);
            const meetingDetails = response.data.meetingDetails || [];
            if (type === 'upcoming') {
               this.upcomingDataSource = new MatTableDataSource(meetingDetails);
               this.dataSource = this.upcomingDataSource;
            } else {
               this.historyDataSource = new MatTableDataSource(meetingDetails);
               this.dataSource = this.historyDataSource;
            }
            this.handleMeetingDataResponse(meetingDetails);
            this.isLoading = false;
            this.applyPendingSearchHighlight();
         },
         error: (error) => {
            this.isLoading = false;
            const httpError = error as HttpErrorResponse;
            console.warn('Scheduling meetings fetch failed', {
               route: this.router.url,
               tab: type,
               status: httpError?.status,
               message: httpError?.message,
            });
         }
      });
   }

   updateStats(data: any): void {
      this.meetingStats = [
         { count: data.totalMeetings || 0, label: 'Total Meetings', icon: 'event', colorClass: 'blue' },
         { count: data.todaysMeetings || 0, label: "Today's Meetings", icon: 'today', colorClass: 'purple' },
         { count: data.upcomingMeetings || 0, label: 'Upcoming Meetings', icon: 'schedule', colorClass: 'green' },
         { count: data.pastMeetings || 0, label: 'Past Meetings', icon: 'history', colorClass: 'orange' },
      ];
   }

   handleMeetingDataResponse(data: any[]): void {
      this.setDataSourceAttributes();
      this.meetingList = data || []
      this.setupTableData(this.meetingList)
   }

   setupTableData(meetingList: any[]): void {
      this.dataSource = new MatTableDataSource(meetingList);
      this.setDataSourceAttributes();

      // Custom sortData for correct sorting of date and other columns
      this.dataSource.sortData = (data: any[], sort: any) => {
         const active = sort.active;
         const direction = sort.direction;
         if (!active || direction === '') return data;
         return data.slice().sort((a, b) => {
            let valueA, valueB;
            if (active === 'date') {
               valueA = a.scheduledDate instanceof Date ? a.scheduledDate.getTime() : new Date(a.scheduledDate).getTime();
               valueB = b.scheduledDate instanceof Date ? b.scheduledDate.getTime() : new Date(b.scheduledDate).getTime();
            } else if (active === 'subject') {
               valueA = a.meetingSubject || '';
               valueB = b.meetingSubject || '';
            } else if (active === 'hospital') {
               valueA = a.hospitalName || '';
               valueB = b.hospitalName || '';
            } else {
               valueA = a[active];
               valueB = b[active];
            }
            if (valueA == null) valueA = '';
            if (valueB == null) valueB = '';
            let comparison = 0;
            if (typeof valueA === 'number' && typeof valueB === 'number') {
               comparison = valueA - valueB;
            } else {
               comparison = valueA.toString().localeCompare(valueB.toString());
            }
            return direction === 'asc' ? comparison : -comparison;
         });
      };

      // Enhanced filterPredicate with hospitalName logic (like goals-page)
      this.dataSource.filterPredicate = (row: any, filter: string) => {
         let filterObj: any = {};
         try {
            filterObj = JSON.parse(filter);
         } catch (e) {
            filterObj = { search: filter };
         }

         const search = (filterObj.search || '').toString().toLowerCase();
         const hospitalDisplayNames = (filterObj.hospitalDisplayNames || []).map((h: any) => (h || '').toString().toLowerCase().trim());
         const duration = (filterObj.duration || '').toString().toLowerCase();
         const attendees = (filterObj.attendees || []).map((a: any) => (a || '').toString().toLowerCase());

         // attendeesNames is an array, so check if any attendee matches the filter
         const rowAttendees = Array.isArray(row.attendeesNames) ? row.attendeesNames : [];
         const attendeesString = rowAttendees.join(', ').toLowerCase();

         const matchesSearch = !search ||
            (row.subject || '').toString().toLowerCase().includes(search) ||
            (row.hospitalName || '').toString().toLowerCase().includes(search) ||
            attendeesString.includes(search);

         // Use displayName for filtering, fallback to hospitalName
         const rowDisplayName = (row.displayName || row.hospitalName || '').toString().toLowerCase().trim();
         const matchesHospital = hospitalDisplayNames.length === 0 || hospitalDisplayNames.includes(rowDisplayName);
         const matchesDuration = !duration || (row.duration || '').toString().toLowerCase() === duration;
         // For attendee filter, match if any attendee matches any selected filter value
         const matchesAttendees = attendees.length === 0 || rowAttendees.some((a: string) => attendees.includes(a.toLowerCase()));

         return matchesSearch && matchesHospital && matchesDuration && matchesAttendees;
      };

      // Apply initial filter
      this.applyFilters();
   }

   get uniqueHospitals() {
      if (!this.meetingList) return []
      return this.meetingList.map((r: any) => r.hospital).filter((v: any, i: any, a: any) => a.indexOf(v) === i)
   }

   get uniqueDurations() {
      if (!this.meetingList) return []
      return this.meetingList.map((r: any) => r.duration).filter((v: any, i: any, a: any) => a.indexOf(v) === i)
   }

   get uniqueAttendees() {
      if (!this.meetingList) return [];
      // Flatten all attendeesNames arrays, deduplicate, and return unique names
      const allNames = this.meetingList
         .filter((r: any) => Array.isArray(r.attendeesNames))
         .flatMap((r: any) => r.attendeesNames);
      return Array.from(new Set(allNames));
   }

   applyFilters(): void {
      // Extract displayNames from selectedHospitals (objects or strings)
      const hospitalDisplayNames = (this.selectedHospitals || []).map((h: any) => {
         if (typeof h === 'string') {
            return h;
         }
         return h?.displayName || h?.hospitalName || h?.name || '';
      }).filter((n: string) => !!n);
      const filterObj = {
         search: this.globalFilter || '',
         hospitalDisplayNames: hospitalDisplayNames,
         duration: this.selectedDuration || '',
         attendees: this.selectedAttendees || [],
      }
      this.dataSource.filter = JSON.stringify(filterObj)
      if (this.dataSource.paginator) {
         this.dataSource.paginator.firstPage()
      }
   }

   resetFilters(): void {
      this.selectedHospitals = []
      this.selectedDuration = ''
      this.selectedAttendees = []
      this.globalFilter = ''
      this.applyFilters()
      if (this.dataSource && this.dataSource.paginator) {
         this.dataSource.paginator.firstPage()
      }
   }

    openNewMeetingDialog(): void {
       const dialogRef = this.dialog.open(NewMeetingSchedulerComponent, {
          width: '850px',
          maxWidth: '95vw',
          panelClass: 'custom-dialog-container',
          data: { hospitals: this.hospitalList },
          disableClose: false
       })
       dialogRef.afterClosed().subscribe(result => {
    
       const shouldRefreshMeetings =
         result === 'success' ||
         result?.status === 'success' ||
         result?.status === 'updated' ||
         result?.refreshMeetings === true;

       if (shouldRefreshMeetings) {
         this.loadMeetings(this.selectedTab);
       }
     })
    }

    openEditMeetingDialog(meeting: any, event: MouseEvent): void {
       event.stopPropagation();
       const dialogRef = this.dialog.open(NewMeetingSchedulerComponent, {
          width: '850px',
          maxWidth: '95vw',
          panelClass: 'custom-dialog-container',
          data: { hospitals: this.hospitalList, meetingData: meeting },
          disableClose: false
       });
       dialogRef.afterClosed().subscribe(result => {
          const shouldRefresh =
             result?.status === 'updated' ||
             result?.refreshMeetings === true;
          if (shouldRefresh) {
             this.loadMeetings(this.selectedTab);
          }
       });
    }
     joinMeeting(url: string): void {
       window.open(url, '_blank');
  }
   deleteMeeting(row: any, event: MouseEvent): void {
      event.stopPropagation();
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
         width: '400px',
         data: {
            title: 'Cancel Meeting',
            message: `Are you sure you want to cancel "${row.meetingSubject || 'this meeting'}"? This action cannot be undone.`,
            confirmButtonText: 'Yes, Cancel Meeting',
            cancelButtonText: 'No, Keep It'
         }
      });
      dialogRef.afterClosed().subscribe(result => {
         if (!result) return;
         this.leaderService.deleteMeeting(row.meetingId).subscribe({
            next: (res: any) => {
               const meta = res?.resultDetails || res?.MetaData?.resultDetails;
               const code = meta?.[0]?.code || meta?.[0]?.status;
               if (code === 'R00' || code === 'SUCCESS' || res?.status === 'SUCCESS') {
                  this.leaderService.showNotification('Meeting cancelled successfully!', false);
                  this.loadMeetings(this.selectedTab);
               } else {
                  this.leaderService.showNotification('Failed to cancel meeting.', true);
               }
            },
            error: () => {
               this.leaderService.showNotification('Error cancelling meeting.', true);
            }
         });
      });
   }

   isMeetingPast(meeting: any): boolean {
      if (!meeting?.scheduledDate) return false;
      const dateStr = meeting.scheduledDate;
      const timeStr = meeting.endTime || '23:59';
      const meetingEnd = new Date(`${dateStr}T${timeStr}:00`);
      return !isNaN(meetingEnd.getTime()) && meetingEnd.getTime() < Date.now();
   }

   openMeetingDetailDialog(row: any): void {
      const dialogRef = this.dialog.open(MeetingDetailDialogComponent, {
         width: '500px',
         data: row
      });
      dialogRef.afterClosed().subscribe(result => {
         const shouldRefreshMeetings =
            result === 'deleted' ||
            result?.status === 'deleted' ||
            result?.status === 'updated' ||
            result?.refreshMeetings === true;

         if (shouldRefreshMeetings) {
            this.loadMeetings(this.selectedTab);
         }
      });
   }

}
